import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import Reveal from "@/components/Reveal";

// Copy supplied verbatim by the owner (2026-09-28). Preserve the six-step
// pathway order, the 80% assessment, the 12-month term, certification vs
// licensing, and authorization vs scope of practice. No CCCHAPS, no pricing,
// enrollment dates, CE credits, accreditation, legal guarantees, testimonials,
// statistics. No verification tool, payments, or downloadable legal documents.

const ctaCls =
  "inline-flex items-center gap-2 bg-navy text-ivory px-7 py-3.5 rounded-full text-sm font-medium hover:bg-navy-light transition-colors duration-200 whitespace-nowrap";
const ctaGoldCls =
  "inline-flex items-center gap-2 bg-gold text-navy px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-gold-hover transition-colors duration-200 whitespace-nowrap";

function Hero() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 pt-36 pb-20 sm:pt-44">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
          Certified From Diagnosis to Goodbye Facilitator
        </p>
        <h1 className="mt-5 max-w-4xl font-serif text-4xl sm:text-5xl lg:text-6xl text-navy tracking-tight leading-[1.08]">
          Certification Requirements &amp; Program Authorization
        </h1>
        <div className="mt-8 max-w-3xl space-y-5 text-lg leading-relaxed text-stone2">
          <p>
            Becoming a Certified From Diagnosis to Goodbye Facilitator means
            more than completing a course.
          </p>
          <p>
            Certification represents demonstrated understanding of the
            program's facilitation methodology, commitment to its ethical
            standards, and authorization to responsibly facilitate the
            approved From Diagnosis to Goodbye program.
          </p>
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <div
          className="mt-16 bg-navy-deep rounded-md px-8 sm:px-14 py-14 sm:py-20 text-center"
          data-testid="certification-hero-statement"
        >
          <p className="max-w-3xl mx-auto font-serif italic text-2xl sm:text-4xl leading-snug text-ivory">
            &ldquo;Certification is not ownership. Certification is{" "}
            <em className="text-gold">authorization</em> to responsibly carry
            the work.&rdquo;
          </p>
        </div>
      </Reveal>
    </section>
  );
}

const FRAMEWORK = ["NOTICE", "LISTEN", "UNDERSTAND", "RESPOND", "HONOR"];

function StepCard({ n, title, children, testid }) {
  return (
    <div
      className="h-full bg-white border border-line rounded-md p-8 hover:border-gold/60 transition-all duration-300"
      data-testid={testid}
    >
      <span className="font-serif text-gold text-2xl">{n}</span>
      <h3 className="mt-4 font-serif text-xl sm:text-2xl text-navy leading-snug">
        {title}
      </h3>
      <div className="mt-4 space-y-4 text-stone2 leading-relaxed text-[15px]">
        {children}
      </div>
    </div>
  );
}

function Path() {
  return (
    <section className="bg-sand border-y border-line">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            The Certification Path
          </p>
          <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-navy tracking-tight">
            Six steps from first session to credential.
          </h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <Reveal className="h-full">
            <StepCard n="1" title="Complete the Five-Session Training" testid="certification-step-1">
              <p>
                Complete The Five Voices of the Journey — Certified
                Facilitator Training for From Diagnosis to Goodbye
                (FD2G-FAC-001).
              </p>
              <p className="flex flex-wrap items-center gap-x-2 gap-y-2">
                {FRAMEWORK.map((w, i) => (
                  <span key={w} className="flex items-center gap-2">
                    <span className="font-serif text-sm text-navy tracking-[0.1em] border border-line-strong rounded-full px-3 py-1 bg-ivory">
                      {w}
                    </span>
                    {i < FRAMEWORK.length - 1 && (
                      <span className="text-gold font-serif">→</span>
                    )}
                  </span>
                ))}
              </p>
              <p>
                The training prepares facilitators to recognize the voices in
                the room and responsibly apply the Five Voices Framework.
              </p>
              <Link
                to="/facilitators/training"
                data-testid="certification-cta-training"
                className="inline-flex items-center gap-2 text-sm text-navy hover:text-gold transition-colors duration-200"
              >
                Explore Facilitator Training <ArrowRight className="w-4 h-4" />
              </Link>
            </StepCard>
          </Reveal>

          <Reveal delay={0.06} className="h-full">
            <StepCard n="2" title="Complete the Session Assessments" testid="certification-step-2">
              <p>
                Each learning session includes scenario-based assessment
                designed to evaluate understanding and practical application.
              </p>
              <p>
                Participants must complete the required assessments before
                proceeding to final certification.
              </p>
            </StepCard>
          </Reveal>

          <Reveal delay={0.12} className="h-full">
            <StepCard n="3" title="Pass the Final Facilitator Certification Assessment" testid="certification-step-3">
              <p>
                After completing all five sessions, participants complete a
                final scenario-based Facilitator Certification Assessment.
              </p>
              <p>
                <span className="inline-block text-xs uppercase tracking-[0.18em] text-mist border border-line-strong rounded-full px-4 py-1.5 bg-ivory">
                  Passing score: 80%
                </span>
              </p>
              <p>
                The assessment focuses on practical facilitator judgment
                rather than memorization alone.
              </p>
              <p>
                Participants may review instructional feedback and retake the
                assessment when needed.
              </p>
            </StepCard>
          </Reveal>

          <Reveal className="h-full">
            <StepCard n="4" title="Affirm the Certified Facilitator Code of Ethics" testid="certification-step-4">
              <p>
                Every facilitator must read and affirm the current Certified
                From Diagnosis to Goodbye Facilitator Code of Ethics.
              </p>
              <p>
                The Code establishes expectations concerning participant
                autonomy, respectful facilitation, privacy, boundaries,
                spiritual and cultural differences, professional scope,
                program integrity, intellectual property, and accurate
                representation of certification.
              </p>
            </StepCard>
          </Reveal>

          <Reveal delay={0.06} className="h-full">
            <StepCard n="5" title="Accept the Authorized Facilitator Agreement" testid="certification-step-5">
              <p>
                Certification requires acceptance of the current Authorized
                Facilitator Agreement.
              </p>
              <p>
                The agreement establishes the terms under which a Certified
                From Diagnosis to Goodbye Facilitator may use and facilitate
                the approved program, curriculum, methodology, and authorized
                resources.
              </p>
            </StepCard>
          </Reveal>

          <Reveal delay={0.12} className="h-full">
            <StepCard n="6" title="Receive Your Certification" testid="certification-step-6">
              <p>
                After all certification requirements have been satisfied, the
                participant receives the credential:
              </p>
              <p className="font-serif text-navy text-lg leading-snug">
                Certified From Diagnosis to Goodbye Facilitator
              </p>
              <p>
                Certification includes an individual certification number and
                certification period.
              </p>
              <p>
                <span className="inline-block text-xs uppercase tracking-[0.18em] text-mist border border-line-strong rounded-full px-4 py-1.5 bg-ivory">
                  Certification term: 12 months
                </span>
              </p>
              <p>
                Certification must remain active for the individual to
                represent themselves as a currently Certified From Diagnosis
                to Goodbye Facilitator and exercise the authorization
                associated with the credential.
              </p>
            </StepCard>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

const SETTINGS = [
  "Hospice and palliative-care environments",
  "Healthcare organizations",
  "Senior-living communities",
  "Churches and ministry settings",
  "Bereavement programs",
  "Caregiver programs",
  "Support groups",
  "Community organizations",
  "Educational and professional-development settings where appropriate",
];

const NOT_AUTHORIZED = [
  "Practice psychotherapy or clinical counseling",
  "Provide medical or healthcare advice",
  "Represent themselves as a licensed healthcare professional",
  "Represent themselves as a professional chaplain solely because of this certification",
  "Provide crisis or mental-health services beyond their existing qualifications",
  "Train or certify other From Diagnosis to Goodbye facilitators unless separately authorized",
  "Rewrite, rebrand, reproduce, sell, sublicense, or create derivative versions of the protected program or curriculum",
  "Represent an organization as licensed merely because one employee or member is certified",
];

function Authorizes() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
      <div className="grid lg:grid-cols-2 gap-12 items-start">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            What Certification Authorizes
          </p>
          <h2 className="mt-5 font-serif text-3xl sm:text-4xl text-navy tracking-tight leading-[1.15]">
            During an active certification period and subject to current
            program standards, a Certified From Diagnosis to Goodbye
            Facilitator may facilitate approved From Diagnosis to Goodbye
            conversations, groups, workshops, and learning experiences using
            authorized program materials.
          </h2>
          <p className="mt-8 text-stone2 leading-relaxed">
            Certification may support facilitation in appropriate settings
            such as:
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            {SETTINGS.map((s) => (
              <span
                key={s}
                className="bg-sand border border-line rounded-full px-5 py-2.5 text-sm text-navy"
              >
                {s}
              </span>
            ))}
          </div>
          <div className="mt-10 max-w-xl font-serif text-xl text-navy leading-relaxed">
            <p>
              Certification authorizes responsible facilitation of the
              approved program.
            </p>
            <p className="mt-3">
              It does not transfer ownership of the program, curriculum,
              methodology, intellectual property, trademarks, books,
              workbooks, or other protected materials.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div
            className="bg-sand border border-line rounded-md p-8"
            data-testid="certification-not-authorize"
          >
            <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
              What Certification Does Not Authorize
            </p>
            <p className="mt-5 text-stone2 leading-relaxed">
              Certification does not independently authorize a facilitator
              to:
            </p>
            <ul className="mt-4 space-y-3">
              {NOT_AUTHORIZED.map((t) => (
                <li key={t} className="flex gap-3 leading-relaxed text-[15px] text-stone2">
                  <span className="shrink-0 font-serif text-gold">—</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.05}>
        <blockquote
          className="mt-16 max-w-4xl border-l-2 border-gold pl-6 sm:pl-8 font-serif italic text-2xl sm:text-3xl text-navy leading-snug"
          data-testid="certification-scope-statement"
        >
          &ldquo;The credential expands authorization to facilitate the From
          Diagnosis to Goodbye program. It does not expand a person's
          professional scope of practice.&rdquo;
        </blockquote>
        <p className="mt-6 max-w-3xl text-stone2 leading-relaxed">
          Individuals who already hold professional credentials remain
          responsible for practicing according to the laws, ethical
          standards, organizational policies, and scope requirements
          applicable to those credentials.
        </p>
      </Reveal>
    </section>
  );
}

const ETHICS = [
  { n: "1", t: "Protect the Person's Voice", b: "Keep the lived experience of the participant at the center of facilitation." },
  { n: "2", t: "Respect Autonomy", b: "Honor each person's right to speak, remain silent, participate differently, or decline to disclose." },
  { n: "3", t: "Facilitate Without Judgment", b: "Create room for experiences and perspectives without imposing conclusions." },
  { n: "4", t: "Honor Spiritual and Cultural Differences", b: "Respect beliefs, traditions, cultures, values, and worldviews that may differ from the facilitator's own." },
  { n: "5", t: "Protect Privacy", b: "Handle personal stories and disclosures responsibly and communicate appropriate privacy expectations." },
  { n: "6", t: "Maintain Appropriate Boundaries", b: "Remain within the facilitator role and within any professional scope the facilitator independently holds." },
  { n: "7", t: "Recognize When Facilitation Is Not Enough", b: "Identify situations requiring referral, additional professional support, organizational procedures, or appropriate safety response." },
  { n: "8", t: "Protect Program Integrity", b: "Use the approved program responsibly and according to current standards." },
  { n: "9", t: "Respect Intellectual Property", b: "Do not improperly reproduce, distribute, modify, sell, or appropriate protected program materials." },
  { n: "10", t: "Represent Certification Accurately", b: "Do not exaggerate what the credential means or claim qualifications the certification does not provide." },
  { n: "11", t: "Maintain Current Certification", b: "Complete required renewal and do not represent an expired credential as active." },
  { n: "12", t: "Use the Facilitator Role With Integrity", b: "Carry the program in a manner consistent with its purpose, standards, and responsibility to participants." },
];

function Ethics() {
  return (
    <section className="bg-sand border-y border-line">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            The Certified Facilitator Code of Ethics
          </p>
          <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-navy tracking-tight">
            Every Certified From Diagnosis to Goodbye Facilitator agrees to
            uphold twelve core responsibilities.
          </h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {ETHICS.map((e, i) => (
            <Reveal key={e.n} delay={0.04 * i} className="h-full">
              <div
                className="h-full bg-ivory border border-line rounded-md p-7"
                data-testid={`certification-ethics-${e.n}`}
              >
                <span className="font-serif text-gold text-xl">{e.n}</span>
                <h3 className="mt-3 font-serif text-lg text-navy leading-snug">
                  {e.t}
                </h3>
                <p className="mt-3 text-sm text-stone2 leading-relaxed">{e.b}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-10 max-w-3xl text-stone2 leading-relaxed">
            The complete current Code of Ethics is presented for formal
            review and affirmation during the certification process and again
            as required during renewal.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Individual() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
          Certification Belongs to the Individual
        </p>
        <div className="mt-6 max-w-3xl space-y-5 text-lg leading-relaxed text-stone2">
          <p>
            A Certified From Diagnosis to Goodbye Facilitator credential is
            issued to an individual.
          </p>
          <p>
            It does not automatically license the facilitator's employer,
            church, hospice, healthcare organization, business, ministry, or
            other organization to formally adopt or offer the program
            organization-wide.
          </p>
        </div>
        <div
          className="mt-12 grid sm:grid-cols-2 gap-6 max-w-3xl"
          data-testid="certification-distinction"
        >
          <div className="border border-gold/40 rounded-md px-7 py-8">
            <p className="font-serif text-2xl sm:text-3xl text-navy leading-snug">
              A facilitator is <em className="text-gold">certified</em>.
            </p>
          </div>
          <div className="border border-gold/40 rounded-md px-7 py-8">
            <p className="font-serif text-2xl sm:text-3xl text-navy leading-snug">
              An organization is <em className="text-gold">licensed</em>.
            </p>
          </div>
        </div>
        <p className="mt-10 max-w-3xl text-stone2 leading-relaxed">
          Organizations seeking formal implementation should review the
          Organizational Licensing program.
        </p>
        <Link
          to="/facilitators/organizations"
          data-testid="certification-cta-organizations"
          className={`${ctaCls} mt-8`}
        >
          Explore Organizational Licensing <ArrowRight className="w-4 h-4" />
        </Link>
      </Reveal>
    </section>
  );
}

const STATUSES = [
  { s: "ACTIVE", d: "Certification is current." },
  { s: "RENEWAL DUE", d: "The facilitator is approaching the certification expiration date and remains active during the designated renewal window." },
  { s: "EXPIRED", d: "The certification period has ended and authorization associated with active certification is no longer current." },
  { s: "SUSPENDED", d: "Certification authorization has been temporarily suspended under applicable program standards." },
  { s: "REVOKED", d: "Certification authorization has been withdrawn under applicable program standards." },
  { s: "VOLUNTARILY INACTIVE", d: "The facilitator has chosen not to maintain active certification." },
];

function Period() {
  return (
    <section className="bg-navy-deep" data-testid="certification-period-band">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
                Your Certification Period
              </p>
              <div className="mt-8 inline-block border border-gold/40 rounded-md px-10 py-8 text-center">
                <p className="text-xs uppercase tracking-[0.24em] text-ivory/70">
                  Initial certification is valid for
                </p>
                <p className="mt-3 font-serif text-4xl sm:text-5xl text-gold">
                  12 Months
                </p>
              </div>
              <p className="mt-8 text-ivory/70 leading-relaxed">
                Certified facilitators are responsible for maintaining active
                status through annual renewal.
              </p>
              <p className="mt-4 text-ivory/70 leading-relaxed">
                The renewal process helps ensure facilitators remain familiar
                with current program standards, ethical expectations,
                methodology, and program updates.
              </p>
              <p className="mt-4 text-ivory/70 leading-relaxed">
                Renewal is designed as a focused annual process rather than
                repeating the entire initial certification course when
                certification remains current.
              </p>
              <div
                className="mt-8 border-t border-ivory/15 pt-8"
                data-testid="certification-active-facilitation-overview"
              >
                <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
                  Active Facilitation Requirement
                </p>
                <p className="mt-4 text-ivory/70 leading-relaxed">
                  To maintain active certification, facilitators must complete
                  at least five approved facilitation sessions during each
                  12-month certification period. Each session must be recorded
                  in an Annual Facilitator Activity Log and supported by
                  acceptable verification as described in the Facilitator
                  Renewal requirements.
                </p>
                <p className="mt-4 text-ivory/70 leading-relaxed">
                  Full documentation standards, confidentiality requirements,
                  exceptions, and how this requirement fits into annual
                  renewal are described on the Facilitator Renewal page.
                </p>
              </div>
              <Link
                to="/facilitators/renewal"
                data-testid="certification-cta-renewal"
                className={`${ctaGoldCls} mt-10`}
              >
                Learn About Annual Renewal <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.1}>
              <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
                Certification Status
              </p>
              <p className="mt-5 text-ivory/70 leading-relaxed">
                As the facilitator system develops, certification records may
                reflect statuses such as:
              </p>
              <div className="mt-6">
                {STATUSES.map((s) => (
                  <div
                    key={s.s}
                    className="border-t border-ivory/15 py-4"
                    data-testid={`certification-status-${s.s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    <p className="font-serif text-gold tracking-[0.1em]">{s.s}</p>
                    <p className="mt-1 text-sm text-ivory/70 leading-relaxed">
                      {s.d}
                    </p>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-sm text-ivory/60 leading-relaxed">
                A future verification system may allow certification status
                to be confirmed using an individual certification number
                while limiting unnecessary disclosure of personal
                information.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function Integrity() {
  return (
    <section className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
          Certification and Program Integrity
        </p>
        <div className="mt-6 max-w-3xl space-y-5 text-lg leading-relaxed text-stone2">
          <p>
            Certification exists partly to protect the people participating
            in From Diagnosis to Goodbye programs and partly to protect the
            integrity of the work itself.
          </p>
          <p>
            The stories and conversations involved in serious illness,
            caregiving, dying, and grief can be deeply personal.
          </p>
          <p>People deserve facilitators who understand that responsibility.</p>
        </div>
        <div className="mt-10 max-w-3xl">
          <p className="text-stone2">The certification process therefore asks more than:</p>
          <p className="mt-4 font-serif italic text-2xl sm:text-3xl text-navy">
            &ldquo;Did you finish the course?&rdquo;
          </p>
          <p className="mt-6 text-stone2">It also asks:</p>
          <div className="mt-4 space-y-2 font-serif text-xl sm:text-2xl text-navy leading-snug" data-testid="certification-integrity-questions">
            <p>Can you notice the voices in the room?</p>
            <p>Can you listen without forcing disclosure?</p>
            <p>Can you understand the story beneath what is being said?</p>
            <p>Can you respond without taking over, correcting, or fixing?</p>
            <p>Can you honor the person, the story, and the boundaries of the space?</p>
          </div>
          <p className="mt-8 font-serif text-xl text-navy">
            That is the responsibility represented by the credential.
          </p>
        </div>
      </Reveal>
    </section>
  );
}

function Closing() {
  return (
    <section className="bg-sand border-t border-line">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24 text-center">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
            Certified From Diagnosis to Goodbye Facilitator
          </p>
          <p className="mt-8 max-w-3xl mx-auto text-lg text-stone2 leading-relaxed">
            A Certified From Diagnosis to Goodbye Facilitator is an
            individual who has successfully completed the authorized
            facilitator training, demonstrated understanding of the program's
            facilitation methodology, agreed to its ethical and program
            standards, and received time-limited authorization to facilitate
            the approved program.
          </p>
          <p className="mt-6 max-w-3xl mx-auto text-sm text-stone2 leading-relaxed">
            The credential does not independently confer clinical,
            counseling, healthcare, ministry, or professional chaplaincy
            credentials.
          </p>
          <p className="mt-12 font-serif italic text-2xl sm:text-3xl text-navy" data-testid="certification-closing-statement">
            See the person. Hear the voice. Honor the journey.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/facilitators/training"
              data-testid="certification-cta-training-final"
              className={ctaGoldCls}
            >
              Explore Facilitator Training <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/facilitators/renewal"
              data-testid="certification-cta-renewal-secondary"
              className={ctaCls}
            >
              Learn About Annual Renewal <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <Link
            to="/facilitators"
            data-testid="certification-back"
            className="mt-10 inline-flex items-center gap-2 text-sm text-stone2 hover:text-gold transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Facilitator Program
            Overview
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function FacilitatorCertification() {
  return (
    <div className="bg-ivory" data-testid="facilitators-certification-page">
      <Hero />
      <Path />
      <Authorizes />
      <Ethics />
      <Individual />
      <Period />
      <Integrity />
      <Closing />
    </div>
  );
}
